import { StateObservable } from "redux-observable";
import { of } from "rxjs";
import { catchError, filter, ignoreElements, map, mergeMap, tap } from "rxjs/operators";

import { RootState } from "../../../../../../../redux/store";
import { Effect } from "../../../../../../../redux/types/effect.type";
import { isActionType } from "../../../../../../../redux/action.functions";
import { EffectActionsObservable } from "../../../../../../../redux/types/effect-actions-observable.type";

import * as actions from "./flyer-detail-page.actions";
import { FlyerDetailPageActions } from "./flyer-detail-page.actions";
import * as store from "./flyer-detail-page.store";

import * as previewService from "../services/flyer-detail-page-preview.service";
import * as templateService from "../../../../../features/flyer-template/services/flyer-template.service";

import * as positionService from "../services/flyer-detail-page-position.service";
import * as navigationService from "../../../../../../../features/navigation/services/navigation.service";
import { FlyerDetailPageFormPositionEffects } from "../features/flyer-detail-page-form-position/store/flyer-detail-page-form-position.effects";
import { FlyerDetailPageFormPageEffects } from "../features/flyer-detail-page-form-page/store/flyer-detail-page-form-page.effects";
import { FlyerDetailPagePreview } from "../types/flyer-detail-page-preview/flyer-detail-page-preview.interface";
import { FlyerDetailActions } from "../../../store/flyer-detail.actions";
import * as flyerActions from "../../../../../store/flyer.actions";
import { createPermissionsFromFlyerPagePreview } from "../types/flyer-detail-page-preview/flyer-detail-page-preview.functions";
import * as userStore from "../../../../../../../features/user/store/user.store";
import { FlyerDetailPageAssetsEffects } from "../features/flyer-detail-page-assets/store/flyer-detail-page-assets.effects";

const fetchPagePreview: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailPageActions.FetchPagePreview)),
    map((action) => action.payload),
    mergeMap((pageId) =>
      previewService.fetchPagePreview(pageId).pipe(
        map((preview) => actions.fetchPagePreviewSuccess(preview)),
        catchError((error) => of(actions.fetchPagePreviewFailure(error)))
      )
    )
  );

const initPermissions: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerDetailPageActions.InitPermissions)),
    map((action) => ({
      preview: action.payload,
      user: userStore.getUser(state$.value),
    })),
    map(({ preview, user }) =>
      flyerActions.setPermissions(createPermissionsFromFlyerPagePreview(user, preview))
    )
  );

const createPagePosition: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerDetailPageActions.CreatePagePosition)),
    map((action) => ({
      templatePositionId: action.payload,
      pagePreview: store.getPagePreview(state$.value),
    })),
    mergeMap(({ templatePositionId, pagePreview }) =>
      positionService.createPagePosition(pagePreview!.pageId, templatePositionId).pipe(
        map((position) => actions.createPagePositionSuccess(position)),
        catchError((error) => of(actions.createPagePositionFailure(error)))
      )
    )
  );

const createPagePositionSuccess: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerDetailPageActions.CreatePagePositionSuccess)),
    map(() => store.getPageId(state$.value)),
    map((pageId) => actions.fetchPagePreview(pageId!))
  );

const fetchPageTemplate: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailPageActions.FetchPageTemplate)),
    map((action) => action.payload),
    mergeMap((templateId) =>
      templateService.fetchTemplate(templateId).pipe(
        map((template) => actions.fetchPageTemplateSuccess(template)),
        catchError((error) => of(actions.fetchPageTemplateFailure(error)))
      )
    )
  );

const fetchPagePreviewSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailPageActions.FetchPagePreviewSuccess)),
    map((action) => action.payload),
    mergeMap((preview: FlyerDetailPagePreview) =>
      of(actions.fetchPageTemplate(preview.templateId), actions.initPermissions(preview))
    )
  );

const reset: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.ResetState)),
    map(() => actions.reset())
  );

const navigateToFlyerPages: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailPageActions.NavigateToFlyerPages)),
    map((action) => action.payload),
    tap((flyerId) => navigationService.goToFlyerPages(flyerId)),
    ignoreElements()
  );

export const FlyerDetailPageEffects = [
  fetchPagePreview,
  createPagePosition,
  createPagePositionSuccess,
  fetchPagePreviewSuccess,
  fetchPageTemplate,
  initPermissions,
  navigateToFlyerPages,
  reset,
  ...FlyerDetailPageAssetsEffects,
  ...FlyerDetailPageFormPageEffects,
  ...FlyerDetailPageFormPositionEffects,
];
